import { useId } from "react";
import { useHelpDisclosure } from "./HelpDisclosure";

/**
 * A labelled form control (#205).
 *
 * The app was built with placeholders standing in for labels. A
 * placeholder disappears the moment you type into it, so the field stops
 * saying what it is exactly when you are checking your work -- and if you
 * come back to a half-filled form you are reading values with no idea what
 * they are. It is also invisible to a screen reader as a name, and low
 * contrast by design, which is the same complaint already fixed once in
 * #194.
 *
 * So: a real <label>, associated by id, always rendered. The placeholder
 * survives where it earns its place -- as an EXAMPLE ("e.g. North
 * Orchard"), not as the field's name.
 *
 * Usage:
 *   <Field label="Batch number">
 *     {(id) => <input id={id} value={...} onChange={...} />}
 *   </Field>
 *
 * The render-prop shape is deliberate: it hands the caller the generated
 * id so the control keeps every prop it already had, rather than this
 * component trying to clone children and guess which element to put the id
 * on. Wrapping a select, a textarea and a date input all work unchanged.
 */
export function Field({ label, hint, help, required = false, error, children }) {
  const id = useId();
  const hintId = hint ? `${id}-hint` : undefined;
  const errorId = error ? `${id}-error` : undefined;
  /*
   * WHAT THE LABEL MEANS, on demand (asked for after #398).
   *
   * Some fields are named by a term of art -- "withdrawal period" is the
   * one that prompted this. The term is CORRECT and worth keeping: it is
   * what appears on the product label somebody is holding, so renaming it
   * to something friendlier would leave them unable to match the two.
   *
   * But correct is not the same as known, and a keeper in their first
   * season has no idea what it means. A hint always on screen is the
   * usual answer and the wrong one here: it is noise for everybody who
   * does know, on a form they fill repeatedly.
   *
   * So: the term stays, with a "?" beside it that explains on demand.
   */
  // The mechanics live in HelpDisclosure now, because #436 needed the
  // same affordance on a section heading and two copies of the
  // aria-expanded/aria-controls pairing is one copy too many.
  const disclosure = useHelpDisclosure(label);
  return (
    <div style={{ display: "grid", gap: "var(--space-1)" }}>
      {/* The asterisk sits OUTSIDE the <label>, not inside it. Anything
          within the label becomes part of the field's name, so "Amount *"
          or "Amount required" is what the control ends up called -- which
          breaks finding it by its actual name, for tests and for anyone
          navigating by voice or a screen reader.
          The requirement itself is carried by aria-required on the control
          below, which is where assistive tech looks for it. */}
      <span style={{ display: "flex", gap: 4, alignItems: "baseline" }}>
        <label htmlFor={id} style={{ fontSize: "var(--font-size-sm)", fontWeight: 600 }}>
          {label}
        </label>
        {required ? <span style={{ color: "var(--color-danger)" }} aria-hidden="true">*</span> : null}
        {/* OUTSIDE the <label>, exactly as the asterisk is and for the
            same reason: anything within it becomes part of the field's
            accessible name, and "Withdrawal period, what is this?" is
            not what the control is called.

            A real <button>, not a styled span -- it is reached by tab,
            fires on Enter and Space, and announces its state. A tooltip
            on hover would be unreachable on the phone this app is mostly
            used on, and this component is shared. */}
        {help ? disclosure.button : null}
      </span>
      {help ? disclosure.panel(help) : null}
      {children(id, {
        // helpId only while OPEN: describing a control by an element
        // that is not rendered is a dangling reference, and describing it
        // by one that is rendered but hidden reads the explanation to
        // everybody whether they asked or not.
        "aria-describedby": [hintId, disclosure.open ? disclosure.id : null, errorId]
          .filter(Boolean).join(" ") || undefined,
        "aria-required": required || undefined,
        "aria-invalid": error ? true : undefined,
      })}
      {hint ? (
        <span id={hintId} style={{ color: "var(--color-text-muted)", fontSize: "var(--font-size-sm)" }}>
          {hint}
        </span>
      ) : null}
      {error ? (
        // role="alert" so it is announced when it appears, not only when
        // the user happens to tab back onto the field.
        <span id={errorId} role="alert" style={{ color: "var(--color-danger)", fontSize: "var(--font-size-sm)" }}>
          {error}
        </span>
      ) : null}
    </div>
  );
}
